import { useOnboarding, type OnboardingStep } from "@/contexts/OnboardingContext";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  Users,
  Contact,
  Package,
  Bot,
  Beaker,
  Globe,
  BarChart3,
  MessageSquare,
  Calendar,
  Target,
  PartyPopper,
  ChevronDown,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  CheckCircle2, Users, Contact, Package, Bot, Beaker,
  Globe, BarChart3, MessageSquare, Calendar, Target, PartyPopper,
};

const STEP_GROUPS: { label: string; slugs: string[] }[] = [
  { label: "Configuração", slugs: ["account_created", "members", "leads", "products"] },
  { label: "Agente IA", slugs: ["agent", "agent_business", "agent_faq", "agent_behavior", "test_agent"] },
  { label: "Canais", slugs: ["integrations"] },
  { label: "Explore", slugs: ["dashboard", "conversations", "appointments", "crm"] },
];

export function OnboardingChecklist() {
  const {
    steps,
    completedSteps,
    progress,
    showChecklist,
    toggleChecklist,
    finishOnboarding,
    navigateToStep,
    isOnboardingActive,
    activeStepSlug,
  } = useOnboarding();

  if (!isOnboardingActive) return null;

  if (!showChecklist) {
    return (
      <button
        onClick={toggleChecklist}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-3 text-white shadow-2xl hover:shadow-purple-500/30 transition-all hover:scale-105"
      >
        <Sparkles className="h-5 w-5" />
        <span className="text-sm font-semibold">Primeiros Passos</span>
        <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold">
          {progress}%
        </span>
      </button>
    );
  }

  const firstPendingSlug = steps.find(
    (s) => !completedSteps.includes(s.slug) && s.slug !== "conclusion"
  )?.slug;

  // Build sequential number for pending steps (excluding conclusion)
  let pendingCounter = 0;
  const pendingNumberMap: Record<string, number> = {};
  steps.forEach((s) => {
    if (s.slug === "conclusion") return;
    if (!completedSteps.includes(s.slug)) {
      pendingCounter++;
      pendingNumberMap[s.slug] = pendingCounter;
    }
  });

  // Build a set of visible slugs for fast lookup
  const visibleSlugs = new Set(steps.map((s) => s.slug));

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-2rem)] animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="rounded-2xl border bg-card shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-white">
              <Sparkles className="h-5 w-5" />
              <h3 className="font-bold text-base">Primeiros Passos</h3>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={finishOnboarding}
                className="rounded-lg px-2.5 py-1 text-xs text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                Pular tutorial
              </button>
              <button
                onClick={toggleChecklist}
                className="rounded-lg p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Progress value={progress} className="h-2 flex-1 bg-white/20 [&>div]:bg-white" />
            <span className="text-xs font-bold text-white/90 whitespace-nowrap">
              {progress}%
            </span>
          </div>
        </div>

        {/* Steps list with groups */}
        <div className="overflow-y-auto max-h-[60vh]">
          <div className="p-3 space-y-0.5">
            {STEP_GROUPS.map((group, gi) => {
              const groupSteps = group.slugs
                .map((slug) => steps.find((s) => s.slug === slug))
                .filter(Boolean) as OnboardingStep[];
              if (groupSteps.length === 0) return null;

              return (
                <div key={group.label}>
                  {/* Group label */}
                  <p className={cn(
                    "text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 px-3",
                    gi === 0 ? "pt-1 pb-1" : "pt-3 pb-1"
                  )}>
                    {group.label}
                  </p>

                  {groupSteps.map((step) => {
                    const done = completedSteps.includes(step.slug);
                    const IconComp = ICON_MAP[step.icon] || CheckCircle2;
                    const isConclusion = step.slug === "conclusion";
                    const isNextStep = step.slug === firstPendingSlug;
                    const isActive = step.slug === activeStepSlug;
                    const stepNumber = pendingNumberMap[step.slug];

                    if (isConclusion) return null;

                    return (
                      <button
                        key={step.slug}
                        onClick={() => {
                          if (step.route) navigateToStep(step);
                        }}
                        disabled={!step.route}
                        className={cn(
                          "w-full flex items-center gap-3 rounded-xl p-3 text-left transition-all",
                          done
                            ? step.route
                              ? "opacity-80 cursor-pointer hover:bg-accent/40"
                              : "opacity-80"
                            : isActive
                              ? "bg-violet-500/10 ring-1 ring-violet-500/40"
                              : isNextStep
                                ? "bg-accent/70 ring-1 ring-violet-500/30"
                                : "hover:bg-accent/50",
                          !step.route && !done && "cursor-default",
                          step.route && !done && "cursor-pointer"
                        )}
                      >
                        {/* Check / number / icon */}
                        <div className="flex-shrink-0">
                          {done ? (
                            <div className="h-7 w-7 rounded-full bg-green-500 flex items-center justify-center">
                              <CheckCircle2 className="h-4 w-4 text-white" />
                            </div>
                          ) : (
                            <div className={cn(
                              "h-7 w-7 rounded-full border-2 flex items-center justify-center",
                              isActive
                                ? "border-violet-500 bg-violet-500/20"
                                : isNextStep
                                  ? "border-violet-500 bg-violet-500/10"
                                  : "border-muted-foreground/30 bg-muted/30"
                            )}>
                              <span className={cn(
                                "text-xs font-bold",
                                isActive || isNextStep ? "text-violet-500" : "text-muted-foreground/60"
                              )}>
                                {stepNumber}
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className={cn(
                              "text-sm font-medium leading-tight",
                              done ? "text-muted-foreground" : "text-foreground"
                            )}>
                              {step.title}
                            </p>
                            {isActive && !done && (
                              <Badge className="bg-violet-500 text-white text-[10px] px-1.5 py-0 h-4 hover:bg-violet-500">
                                Ativo
                              </Badge>
                            )}
                            {isNextStep && !isActive && (
                              <Badge className="bg-violet-500 text-white text-[10px] px-1.5 py-0 h-4 hover:bg-violet-500">
                                Próximo
                              </Badge>
                            )}
                          </div>
                          {!done && (
                            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                              {step.description}
                            </p>
                          )}
                        </div>
                        {/* Arrow */}
                        {!done && step.route && (
                          <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground/50" />
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })}

            {/* Conclusion button */}
            <div className="pt-2 px-1">
              <Button
                onClick={finishOnboarding}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-xl h-11 shadow-lg"
              >
                <PartyPopper className="h-4 w-4 mr-2" />
                Finalizar tour
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
